<template>
  <CustomModal ref="modal" :title="isEdit ? 'Sujet' : 'Nouveau sujet'" class="topic-modal">
    <div class="container">
      <CustomForm ref="form" :inputs=inputs :submit=buttons.submit @submit="preventNativeSubmit">
        <div class="columns">
          <div class="column">
            <CustomTextField :content=inputs.title />
            <FunnelStageSwitch :content=inputs.funnelStage />
            <p v-if="suggestionText && !inputs.funnelStage.value" class="hint">
              {{ suggestionText }}
              <button type="button" class="link" @click="inputs.funnelStage.value = suggestion.stage">Choisir cette intention</button>
            </p>
            <div class="fields-row">
              <CustomSelectField :content=inputs.cadence />
              <CustomTextField :content=inputs.plannedDate />
            </div>
            <CustomTextAreaField :content=inputs.angle :auto-grow="true" :min-height="56" />
            <CustomTextAreaField :content=inputs.notes :auto-grow="true" :min-height="84" />
          </div>

          <div class="column">
            <h3 class="panel-title">À vérifier avant de publier</h3>
            <ul v-if="checks.length > 0" class="checks">
              <li v-for="(check, index) in checks" :key="index" :class="{done: check.done}">
                <button type="button" class="check-box" role="checkbox" :aria-checked="check.done" :aria-label="check.label" @click="check.done = !check.done">
                  <svg v-if="check.done" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12.5l5 5L20 6.5"/></svg>
                </button>
                <span class="check-label" @click="check.done = !check.done">{{ check.label }}</span>
                <button type="button" class="check-remove" title="Retirer ce point" @click="removeCheck(index)"><span class="fas fa-xmark"></span></button>
              </li>
            </ul>
            <p v-else class="hint">Chiffres, noms, promesses : ce que vous voulez revérifier avant de publier.</p>
            <div class="add-row">
              <input v-model="newCheck" class="text-input" type="text" maxlength="200" placeholder="Ajouter un point à vérifier" aria-label="Nouveau point à vérifier"
                     @keydown.enter.prevent.stop="addCheck" />
              <button type="button" class="panel-button" :disabled="!newCheck.trim() || checks.length >= MAX_CHECKS" @click="addCheck">Ajouter</button>
            </div>

            <h3 class="panel-title">{{ isEdit ? 'Publications' : 'Réseaux' }}</h3>
            <template v-if="isEdit">
              <ul v-if="publications.length > 0" class="publications">
                <li v-for="publication in publications" :key="publication.id">
                  <span class="channel">{{ channelLabel(publication.channel) }}</span>
                  <StatusBadge :status="publication.status" :status-enum="PUBLICATION_STATUS_ENUM" />
                  <StatusBadge :status="publication.text.status" :status-enum="TEXT_STATUS_ENUM" :label="'Texte : ' + labelOf(TEXT_STATUS_ENUM, publication.text.status).toLowerCase()" />
                  <button type="button" class="panel-button open" @click="open(publication.id)">Ouvrir</button>
                </li>
              </ul>
              <p v-else class="hint">Aucune publication : ajoutez un réseau pour rédiger le texte.</p>
              <div v-if="missingChannels.length > 0" class="chips">
                <button v-for="channel in missingChannels" :key="channel.value" type="button" class="chip" :disabled="adding" @click="addPublication(channel.value)">+ {{ channel.label }}</button>
              </div>
            </template>
            <template v-else>
              <div class="chips">
                <button v-for="channel in channelChoices" :key="channel.value" type="button" class="chip" :class="{selected: channels.includes(channel.value)}"
                        :aria-pressed="channels.includes(channel.value)" @click="toggleChannel(channel.value)">{{ channel.label }}</button>
              </div>
              <p class="hint">Une publication est préparée par réseau choisi. Vous pourrez en ajouter plus tard.</p>
            </template>

            <div class="parked">
              <button type="button" class="chip" :class="{selected: parked}" :aria-pressed="parked" @click="parked = !parked">Mettre de côté</button>
              <span class="hint">Le sujet quitte la liste « En cours » sans être supprimé.</span>
            </div>
          </div>
        </div>

        <div class="button-container">
          <CustomButton class="button" @click="submitForm" :params=buttons.submit />
          <CustomButton v-if="!isEdit" class="button" :is-secondary="true" @click="submitAndWrite" :params=writeButton />
          <CustomButton class="button" :is-secondary="true" @click="closeModal()" :params=buttons.cancel />
        </div>
      </CustomForm>
    </div>
  </CustomModal>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
import CustomTextAreaField from "@/components/forms/customs/customTextAreaField/CustomTextAreaField.vue";
import CustomSelectField from "@/components/forms/customs/customSelectField/CustomSelectField.vue";
import CustomForm from "@/components/forms/CustomForm.vue";
import CustomModal from "@/components/modals/CustomModal.vue";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import {NotificationService} from "@vues/v-common";
import {TopicController} from "@/marketing/controllers/TopicController";
import {PublicationController} from "@/marketing/controllers/PublicationController";
import {StatsController} from "@/marketing/controllers/StatsController";
import FunnelStageSwitch from "@/marketing/components/FunnelStageSwitch.vue";
import {
  CADENCE_ENUM, CHANNEL_ENUM, FUNNEL_STAGE_ENUM, PUBLICATION_STATUS_ENUM, TEXT_STATUS_ENUM,
  enumToSelectValues, fromInputDate, idOf, labelOf, suggestStage, toInputDate
} from "@/marketing/utils";
library.add(faCheck, faXmark);

const MAX_CHECKS = 30;

/**
 * Modale d'un sujet : création avec openModal(), modification avec openModal(sujet).
 * callback est appelé après l'enregistrement (ou l'ajout d'une publication), avec l'identifiant du sujet.
 * openPublication(publicationId) ouvre la modale d'une publication (par le tableau, qui porte les deux modales).
 */
export default {
  name: 'TopicModal',
  props: ['callback', 'openPublication'],
  components: {CustomModal, CustomForm, CustomSelectField, CustomTextField, CustomTextAreaField, CustomButton, FunnelStageSwitch, StatusBadge},
  data() {
    return {
      MAX_CHECKS,
      PUBLICATION_STATUS_ENUM,
      TEXT_STATUS_ENUM,
      topicId: undefined,
      publications: [],
      checks: [],
      newCheck: '',
      parked: false,
      channels: [CHANNEL_ENUM.LINKEDIN_PAGE.value],
      channelChoices: enumToSelectValues(CHANNEL_ENUM),
      suggestion: undefined,
      adding: false,
      inputs: {
        title: {label: 'Sujet', value: '', type: 'text', required: true, placeholder: 'Ex : Pourquoi la facture électronique change tout'},
        funnelStage: {label: 'À quoi sert ce contenu ?', value: '', required: true},
        cadence: {label: 'Rythme', value: CADENCE_ENUM.WEEKLY.value, type: 'select', selectValues: enumToSelectValues(CADENCE_ENUM)},
        plannedDate: {label: 'Prévu le', value: '', type: 'date'},
        angle: {label: 'L\'angle en une ou deux phrases', value: '', type: 'text'},
        notes: {label: 'Notes, sources, idées', value: '', type: 'text'},
      },
      buttons: {
        submit: {text: "Enregistrer"},
        write: {text: "Créer et rédiger"},
        cancel: {text: "Annuler"},
      }
    };
  },
  computed: {
    isEdit() {
      return this.topicId !== undefined;
    },
    // Réseaux sans publication pour ce sujet
    missingChannels() {
      return Object.values(CHANNEL_ENUM).filter(channel => !this.publications.some(publication => publication.channel === channel.value));
    },
    // « Créer et rédiger » suit l'état du bouton principal (désactivé tant que le sujet ou l'intention manque)
    writeButton() {
      return {text: this.buttons.write.text, disabled: this.buttons.submit.disabled, loading: this.buttons.submit.loading};
    },
    suggestionText() {
      return this.suggestion
          ? `Conseil : « ${FUNNEL_STAGE_ENUM[this.suggestion.stage].label} » est le plus en retard sur votre objectif (${this.suggestion.share} % pour ${this.suggestion.objective} % visés).`
          : '';
    },
  },
  methods: {
    labelOf,
    channelLabel(channel) {
      return labelOf(CHANNEL_ENUM, channel, channel);
    },
    resetInputs() {
      Object.values(this.inputs).forEach(input => {
        input.value = '';
        input.error = false;
      });
      this.inputs.cadence.value = CADENCE_ENUM.WEEKLY.value;
      this.publications = [];
      this.checks = [];
      this.newCheck = '';
      this.parked = false;
      this.channels = [CHANNEL_ENUM.LINKEDIN_PAGE.value];
      this.suggestion = undefined;
    },
    // Remplit la fiche avec le sujet complet (la ligne du tableau n'en porte qu'une partie)
    fill(full) {
      this.inputs.title.value = full.title ?? '';
      this.inputs.funnelStage.value = full.funnelStage ?? '';
      this.inputs.cadence.value = full.cadence ?? CADENCE_ENUM.WEEKLY.value;
      this.inputs.plannedDate.value = toInputDate(full.plannedDate);
      this.inputs.angle.value = full.angle ?? '';
      this.inputs.notes.value = full.notes ?? '';
      this.checks = (full.checks ?? []).map(check => ({label: check.label, done: check.done === true}));
      this.parked = full.parked === true;
      this.fillPublications(full);
    },
    fillPublications(full) {
      this.publications = (full.publications ?? []).map(publication => ({...publication, id: idOf(publication), text: publication.text ?? {status: 'NONE'}}));
    },
    async openModal(topic) {
      this.topicId = undefined;
      this.resetInputs();
      this.buttons.submit.text = topic ? "Enregistrer" : "Créer";
      this.$refs.modal.openModal();

      if (!topic) {
        // Création : l'intention la plus en retard est proposée, jamais choisie à la place de l'utilisateur
        const report = await StatsController.funnel();
        if (report && !report.errors && !this.isEdit) this.suggestion = suggestStage(report);
        return;
      }

      this.topicId = idOf(topic);
      const full = await TopicController.get(this.topicId);
      if (!full || full.errors) {
        this.topicId = undefined;
        this.closeModal();
        return;
      }
      this.fill(full);
    },
    // Les publications ont changé (texte, image, diffusion) : l'avancement affiché suit
    async reloadPublications() {
      if (!this.isEdit || !this.$refs.modal.isDisplayed) return;
      const full = await TopicController.get(this.topicId);
      if (full && !full.errors) this.fillPublications(full);
    },
    // Appelé quand la modale d'une publication se referme : celle-ci redevient la modale active
    refocus() {
      if (this.$refs.modal.isDisplayed) this.$refs.modal.openModal();
    },
    closeModal() {
      this.$refs.modal?.closeModal();
    },
    preventNativeSubmit(event) {
      event?.preventDefault?.();
    },
    open(publicationId) {
      if (this.openPublication) this.openPublication(publicationId);
    },
    addCheck() {
      const label = this.newCheck.trim();
      if (!label || this.checks.length >= MAX_CHECKS) return;
      this.checks.push({label, done: false});
      this.newCheck = '';
    },
    removeCheck(index) {
      this.checks.splice(index, 1);
    },
    toggleChannel(channel) {
      this.channels = this.channels.includes(channel) ? this.channels.filter(item => item !== channel) : [...this.channels, channel];
    },
    async addPublication(channel) {
      this.adding = true;
      const res = await PublicationController.post({topicId: this.topicId, channel});
      this.adding = false;
      if (res && !res.errors) {
        (new NotificationService).addNotification("success", `Publication ${this.channelLabel(channel)} ajoutée`);
        await this.reloadPublications();
        if (this.callback) this.callback(this.topicId);
      }
    },
    text(input) {
      return String(input.value ?? '').trim();
    },
    buildBody() {
      const body = {
        title: this.text(this.inputs.title),
        funnelStage: this.inputs.funnelStage.value,
        cadence: this.inputs.cadence.value,
        plannedDate: fromInputDate(this.inputs.plannedDate.value),
        angle: this.text(this.inputs.angle),
        notes: this.text(this.inputs.notes),
        parked: this.parked,
        // Un point en cours de saisie n'est pas perdu
        checks: [...this.checks, ...(this.newCheck.trim() ? [{label: this.newCheck.trim(), done: false}] : [])],
      };
      if (!this.isEdit) body.channels = this.channels;
      return body;
    },
    // Renvoie l'identifiant du sujet enregistré (et ceux de ses publications à la création), ou undefined si l'enregistrement a échoué
    async save(event) {
      this.preventNativeSubmit(event);

      if (!this.text(this.inputs.title)) {
        this.inputs.title.error = true;
        (new NotificationService).addNotification("danger", "Le sujet n'est pas renseigné");
        return;
      }
      if (!this.inputs.funnelStage.value) {
        (new NotificationService).addNotification("danger", "Choisissez à quoi sert ce contenu : se faire connaître, donner confiance ou convertir");
        return;
      }

      const body = this.buildBody();
      const res = await this.$refs.form.submitForm(() => this.isEdit
          ? TopicController.patch(this.topicId, body)
          : TopicController.post(body));
      if (!res || res.errors) return;

      (new NotificationService).addNotification("success", this.isEdit ? "Sujet modifié" : "Sujet créé");
      const id = this.isEdit ? this.topicId : idOf(res);
      this.closeModal();
      if (this.callback) await this.callback(id);
      return {id, publicationIds: res.publicationIds ?? []};
    },
    async submitForm(event) {
      await this.save(event);
    },
    // Création puis ouverture directe de la première publication pour écrire le texte
    async submitAndWrite(event) {
      const saved = await this.save(event);
      if (saved && saved.publicationIds.length > 0) this.open(saved.publicationIds[0]);
    },
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/marketing/style/modal.styl';

.container
  display flex
  flex-direction column

.column
  min-width 0

.hint .link
  margin-left 4px
  padding 0
  border 0
  background none
  color THEME_COLOR_PRIMARY
  font-family inherit
  font-size inherit
  font-weight 700
  text-decoration underline
  cursor pointer

// Liste des points à vérifier : cases à cocher cliquables, la liste reste compacte
.checks
  margin 0 0 8px
  padding 0
  max-height 188px
  overflow-y auto
  list-style none

  li
    display flex
    align-items center
    gap 10px
    padding 5px 2px
    border-bottom 1px solid PAGE_RULE_SOFT

    &.done .check-label
      color CTRL_TEXT_MUTED
      text-decoration line-through

.check-box
  flex 0 0 20px
  width 20px
  height 20px
  padding 0
  border 1.5px solid CTRL_BORDER_HOVER
  border-radius 6px
  background white
  color white
  display flex
  align-items center
  justify-content center
  cursor pointer

  .done &
    border-color THEME_COLOR_PRIMARY
    background THEME_COLOR_PRIMARY

.check-label
  flex 1 1 auto
  min-width 0
  font-size 14px
  line-height 1.3
  overflow-wrap anywhere
  cursor pointer

.check-remove
  flex 0 0 auto
  padding 2px 6px
  border 0
  background none
  color CTRL_TEXT_MUTED
  cursor pointer

  &:hover
    color CTRL_ERROR

.add-row
  display grid
  grid-template-columns 1fr auto
  gap 8px

// Publications du sujet : réseau, statut, texte et bouton d'ouverture sur une ligne
.publications
  margin 0 0 8px
  padding 0
  list-style none

  li
    display flex
    align-items center
    flex-wrap wrap
    gap 8px
    padding 6px 2px
    border-bottom 1px solid PAGE_RULE_SOFT

  .channel
    min-width 74px
    font-weight 700

  .open
    margin-left auto
    height 30px
    padding 0 14px
    font-size 13px

.parked
  margin-top 14px
  display flex
  align-items center
  gap 10px

  .chip
    flex 0 0 auto
    white-space nowrap

  .hint
    margin 0
</style>

<style lang="stylus">
.topic-modal
  > .modal
    width 960px !important
    max-width 100%
    height auto !important
    max-height 94vh
    overflow-y auto
</style>
